import { ARRAY_TYPES } from './tagTypes';
import { createTranslationKey } from './createTranslationKey';

/**
 * Normalizes a label for the search: no case, no accents, no edge spaces.
 *
 * @function
 *
 * @param {string} value - The text to normalize.
 * @returns {string} The comparable text.
 */
export const normalizeSearch = (value) =>
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

// Children are addressed through the array markers, exactly like the panel
// does when it walks the tree.
const childrenPathOf = (item, currentKey) => {
  if (!ARRAY_TYPES.includes(item.type)) return currentKey;

  return item.type === 'array-array-object'
    ? `${currentKey}.index.index`
    : `${currentKey}.index`;
};

/**
 * Adds the translated label and its comparable form to every node, without
 * touching the shape the panel renders (`key`, `type`, `subcontent`).
 *
 * @function
 *
 * @param {Array} tags - The tag tree fetched for the picked use case.
 * @param {Function} t - Translation function from i18next.
 * @param {Function} p - Pluralize function.
 * @param {string} [parentKeyPath] - Path of the parent, markers included.
 * @returns {Array} The decorated tree.
 */
export const decorateTagTree = (tags, t, p, parentKeyPath = '') =>
  (tags || []).map((item) => {
    const currentKey = parentKeyPath
      ? `${parentKeyPath}.${item.key}`
      : item.key;
    const label = createTranslationKey('tag', currentKey, t, p);

    return {
      ...item,
      label,
      search: normalizeSearch(label),
      subcontent: item.subcontent?.length
        ? decorateTagTree(
            item.subcontent,
            t,
            p,
            childrenPathOf(item, currentKey)
          )
        : []
    };
  });

// A matching branch keeps its whole subtree; a branch only leading to matches
// keeps the path down to them.
const filterByNeedle = (nodes, needle) =>
  nodes.flatMap((node) => {
    if (node.search.includes(needle)) return [node];

    const subcontent = filterByNeedle(node.subcontent, needle);

    return subcontent.length ? [{ ...node, subcontent }] : [];
  });

/**
 * Keeps the nodes whose label matches, and the branches leading to them.
 *
 * @function
 *
 * @param {Array} nodes - A decorated tree.
 * @param {string} query - What the author typed.
 * @returns {Array} The pruned tree, the input itself when the query is empty.
 */
export const filterDecoratedTree = (nodes, query) => {
  const needle = normalizeSearch(query);

  return needle ? filterByNeedle(nodes, needle) : nodes;
};

/**
 * Decorates then filters a raw tag tree in one call.
 *
 * @function
 *
 * @param {Array} tags - The tag tree fetched for the picked use case.
 * @param {string} query - What the author typed.
 * @param {Function} t - Translation function from i18next.
 * @param {Function} p - Pluralize function.
 * @returns {Array} The decorated and pruned tree.
 */
export const filterTagTree = (tags, query, t, p) =>
  filterDecoratedTree(decorateTagTree(tags, t, p), query);
